/**
 * AccordionSection Component
 * Generic accordion section that can be used for any data type
 */

import React, { useMemo } from 'react';
import { AccordionSectionProps } from './types';
import { useAccordionState } from './hooks/useAccordionState';
import { LoadingState } from './LoadingState';
import { AccordionItem } from './AccordionItem';
import { ExpandIcon } from './ExpandIcon';
import '../Planets/Planets.css';

/**
 * Generic AccordionSection component
 * @template T - Type of data extracted from API response
 */
export function AccordionSection<T>({
  apiResponse,
  className = '',
  dataExtractor,
  itemsMapper,
  defaultExpandedSections = [],
  loadingMessage,
  idPrefix,
  children,
  customDetailRenderer,
  onAccordionChange,
  customHeader
}: AccordionSectionProps<T> & {
  children?: React.ReactNode;
  customDetailRenderer?: (item: any, isExpanded: boolean, onToggle: () => void, idPrefix: string) => React.ReactNode;
  customHeader?: React.ReactNode | ((item: any, isItemExpanded: boolean) => React.ReactNode);
}) {

  // Extract data from API response
  const extractedData = useMemo(() => {
    return apiResponse ? dataExtractor(apiResponse) : null;
  }, [apiResponse, dataExtractor]);

  // Map data to accordion items
  const accordionItems = useMemo(() => {
    return extractedData ? itemsMapper(extractedData) : [];
  }, [extractedData, itemsMapper]);

  // Manage accordion state with persistence callback
  const { isExpanded, toggleSection, isAnimating } = useAccordionState(defaultExpandedSections, onAccordionChange);

  // Show loading state if no data
  if (!extractedData || accordionItems.length === 0) {
    return <LoadingState message={loadingMessage} className={className} />;
  }

  return (
    <div className={`planets-container ${className}`}>
      <div className="planets-accordion">
        {accordionItems.map((item) => {
          if (customDetailRenderer) {
            // Use custom renderer for complex cases - section headers (not planets)
            return (
              <div key={item.id} className="section-item">
                {/* Level 1 - Section header (always visible) */}
                <div
                  className="section-header"
                  onClick={() => toggleSection(item.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded(item.id)}
                  aria-controls={`${idPrefix}-details-${item.id}`}
                  data-accordion-id={item.id}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSection(item.id);
                    }
                  }}
                >
                  <div className="section-expand-icon">
                    <ExpandIcon isExpanded={isExpanded(item.id)} />
                  </div>

                  <div className="section-header-content">
                    <span className="section-title">
                      {typeof customHeader === 'function' ? customHeader(item, isExpanded(item.id)) : (customHeader || item.name)}
                    </span>
                  </div>
                </div>

                {/* Level 2 - Custom Details */}
                {isExpanded(item.id) && (
                  <div
                    className={`section-details-container${isAnimating(item.id) ? ' expanding' : ''}`}
                    id={`${idPrefix}-details-${item.id}`}
                    role="region"
                    aria-labelledby={`${idPrefix}-summary-${item.id}`}
                  >
                    {customDetailRenderer(item, isExpanded(item.id), () => toggleSection(item.id), idPrefix)}
                  </div>
                )}
              </div>
            );
          }

          // Use standard AccordionItem for simple cases
          return (
            <AccordionItem
              key={item.id}
              item={item}
              isExpanded={isExpanded(item.id)}
              isAnimating={isAnimating(item.id)}
              onToggle={() => toggleSection(item.id)}
              idPrefix={idPrefix}
            />
          );
        })}
      </div>
      {children}
    </div>
  );
}

export default AccordionSection;